import React from 'react';
import { cn } from '~/lib/utils';
import styles from './MessageAttachmentRow.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface MessageAttachmentRowProps {
  /**
   * The chips that were sent with the message — `AttachmentCard`s, 96x96, two of them in the one
   * artboard that draws this, *With attachments*. They carry no `onRemove`: the message is sent,
   * so the dot is the picture Paper drew and not a control.
   */
  children: React.ReactNode;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The row of attachment chips that rides **above** a user bubble.
 *
 * The only thing in the whole system that sits above a message. Everything else a bubble carries —
 * the clamp's `Show more`, the hover actions, the queue's `Queued` meta — hangs 4px below it. The
 * inversion is the design's argument about what an attachment is: it is not a footnote to the
 * sentence, it is the thing the sentence is about, so you read it first.
 *
 * It shares its 8px gap and its 96px chips with `ComposerAttachmentTray` and nothing else: no
 * height, no padding, no clipping, no scrolling. That is deliberate — the composer has to hold a
 * fixed height while you add files, and a sent message has nothing left to hold still for.
 */
export function MessageAttachmentRow({
  children,
  theme: themeProp,
  className,
}: MessageAttachmentRowProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.row, className)}>{children}</div>
  );
}
